import { useState, useRef, useEffect } from 'react';
import { Link, usePage } from '@inertiajs/react';

const navigation = [
    {
        label: 'Dashboard',
        icon: 'bi-grid-1x2',
        href: '/',
    },
    {
        label: 'CRM',
        icon: 'bi-people',
        children: [
            { icon: 'bi-person-lines-fill', title: 'Clients', desc: 'Client management', href: '/clients' },
            { icon: 'bi-people', title: 'Contacts', desc: 'Manage all contacts', href: '/contacts' },
            { icon: 'bi-building', title: 'Companies', desc: 'Organization management', href: '/companies' },
            { icon: 'bi-currency-dollar', title: 'Deals', desc: 'Sales pipeline', href: '/deals' },
            { icon: 'bi-check2-square', title: 'Tasks', desc: 'Activities & tasks', href: '/tasks' },
            { icon: 'bi-calendar-event', title: 'Meetings', desc: 'Schedule meetings', href: '/meetings' },
            { icon: 'bi-envelope', title: 'Emails', desc: 'Email tracking', href: '/emails' },
        ],
    },
    {
        label: 'Travel',
        icon: 'bi-airplane',
        children: [
            { icon: 'bi-chat-left-text', title: 'Queries', desc: 'Travel queries & leads', href: '/queries' },
            { icon: 'bi-airplane', title: 'Bookings', desc: 'Manage bookings', href: '/bookings' },
            { icon: 'bi-map', title: 'Itineraries', desc: 'Trip planning', href: '/itineraries' },
            { icon: 'bi-box-seam', title: 'Packages', desc: 'Travel packages', href: '/packages' },
            { icon: 'bi-truck', title: 'Suppliers', desc: 'Vendor management', href: '/suppliers' },
            { icon: 'bi-geo-alt', title: 'Destinations', desc: 'Manage locations', href: '/destinations' },
            { icon: 'bi-currency-exchange', title: 'Quotations', desc: 'Price quotes', href: '/quotations' },
        ],
    },
    {
        label: 'Reports',
        icon: 'bi-graph-up',
        children: [
            { icon: 'bi-graph-up', title: 'Analytics', desc: 'Business insights', href: '/analytics' },
            { icon: 'bi-pie-chart', title: 'Sales Report', desc: 'Revenue data', href: '/reports/sales' },
            { icon: 'bi-people', title: 'Team Report', desc: 'Team performance', href: '/reports/team' },
            { icon: 'bi-calendar3', title: 'Booking Report', desc: 'Booking analytics', href: '/reports/bookings' },
        ],
    },
    {
        label: 'Settings',
        icon: 'bi-gear',
        children: [
            { icon: 'bi-person-gear', title: 'Users', desc: 'User management', href: '/users' },
            { icon: 'bi-shield-lock', title: 'Roles & Permissions', desc: 'Access control', href: '/roles' },
            { icon: 'bi-gear', title: 'General Settings', desc: 'App configuration', href: '/settings' },
            { icon: 'bi-palette', title: 'Customization', desc: 'Branding & theme', href: '/settings/customize' },
        ],
    },
];

export default function Topbar({ onSearchOpen, mobileMenuOpen, onMobileMenuToggle }) {
    const { auth, url } = usePage().props;
    const currentUrl = usePage().url;
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);
    const navRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
            if (navRef.current && !navRef.current.contains(e.target)) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isActive = (href) => {
        if (href === '/') return currentUrl === '/';
        return currentUrl.startsWith(href);
    };

    const isGroupActive = (item) => {
        if (item.href) return isActive(item.href);
        return item.children?.some(child => isActive(child.href));
    };

    const getUserInitials = () => {
        if (!auth?.user?.name) return 'U';
        return auth.user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <header className="topbar">
            <div className="topbar-container">
                {/* Brand */}
                <Link href="/" className="topbar-brand">
                    <div className="brand-icon">
                        <i className="bi bi-airplane"></i>
                    </div>
                    <span className="brand-text">Trip<span>Cabinet</span></span>
                </Link>

                {/* Mobile Toggle */}
                <button className="mobile-toggle" onClick={onMobileMenuToggle}>
                    <i className={`bi ${mobileMenuOpen ? 'bi-x-lg' : 'bi-list'}`}></i>
                </button>

                {/* Main Navigation */}
                <nav className={`topbar-nav ${mobileMenuOpen ? 'show' : ''}`} ref={navRef}>
                    {navigation.map((item, idx) => (
                        <div
                            key={idx}
                            className={`nav-item ${isGroupActive(item) ? 'active' : ''}`}
                            onMouseEnter={() => item.children && setActiveDropdown(idx)}
                            onMouseLeave={() => setActiveDropdown(null)}
                        >
                            {item.href ? (
                                <Link href={item.href} className="nav-link">
                                    <i className={`bi ${item.icon}`}></i>
                                    <span>{item.label}</span>
                                </Link>
                            ) : (
                                <>
                                    <button
                                        className="nav-link"
                                        onClick={() => setActiveDropdown(activeDropdown === idx ? null : idx)}
                                    >
                                        <i className={`bi ${item.icon}`}></i>
                                        <span>{item.label}</span>
                                        <i className="bi bi-chevron-down dropdown-arrow"></i>
                                    </button>

                                    <div className={`mega-dropdown ${activeDropdown === idx ? 'show' : ''}`}>
                                        <div className="mega-dropdown-grid">
                                            {item.children.map((child, childIdx) => (
                                                <Link
                                                    key={childIdx}
                                                    href={child.href}
                                                    className={`mega-item ${isActive(child.href) ? 'active' : ''}`}
                                                    onClick={() => setActiveDropdown(null)}
                                                >
                                                    <div className="mega-item-icon">
                                                        <i className={`bi ${child.icon}`}></i>
                                                    </div>
                                                    <div className="mega-item-content">
                                                        <div className="mega-item-title">{child.title}</div>
                                                        <div className="mega-item-desc">{child.desc}</div>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </nav>

                {/* Right Section */}
                <div className="topbar-right">
                    {/* Search */}
                    <button className="topbar-search" onClick={onSearchOpen}>
                        <i className="bi bi-search"></i>
                        <span className="search-text">Search...</span>
                        <kbd>⌘K</kbd>
                    </button>

                    {/* Notifications */}
                    <button className="topbar-icon-btn">
                        <i className="bi bi-bell"></i>
                        <span className="notification-badge">3</span>
                    </button>

                    {/* User Menu */}
                    <div className="user-menu" ref={userMenuRef}>
                        <button
                            className="user-trigger"
                            onClick={() => setUserMenuOpen(!userMenuOpen)}
                        >
                            <div className="user-avatar">
                                {getUserInitials()}
                            </div>
                            <div className="user-info">
                                <span className="user-name">{auth?.user?.name || 'User'}</span>
                                <span className="user-role">Administrator</span>
                            </div>
                            <i className="bi bi-chevron-down"></i>
                        </button>

                        <div className={`user-dropdown ${userMenuOpen ? 'show' : ''}`}>
                            <div className="dropdown-header">
                                <div className="dropdown-user-name">{auth?.user?.name || 'User'}</div>
                                <div className="dropdown-user-email">{auth?.user?.email || 'user@example.com'}</div>
                            </div>
                            <div className="dropdown-body">
                                <Link href="/profile" className="dropdown-item">
                                    <i className="bi bi-person"></i>
                                    My Profile
                                </Link>
                                <Link href="/settings" className="dropdown-item">
                                    <i className="bi bi-gear"></i>
                                    Settings
                                </Link>
                                <Link href="/help" className="dropdown-item">
                                    <i className="bi bi-question-circle"></i>
                                    Help & Support
                                </Link>
                                <hr className="dropdown-divider" />
                                <Link href="/logout" method="post" as="button" className="dropdown-item text-danger">
                                    <i className="bi bi-box-arrow-right"></i>
                                    Sign Out
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}
